import i18next from 'i18next';
import { toast } from 'sonner';

import { isServerUnavailable } from './isServerUnavailable';

/**
 * 이 키를 뮤테이션의 meta 에 넣으면 서버 에러 토스트를 띄우지 않는다.
 *
 * 실패해도 자체적으로 성공 처리하는 뮤테이션을 위한 것. 예: 로그아웃은 서버 호출이
 * 실패해도 로컬 세션을 지우고 홈으로 보내 사용자에겐 성공으로 보이는데, 여기에
 * 에러 토스트까지 뜨면 성공한 동작에 에러가 붙은 것처럼 보인다.
 *
 *   useMutation({ ..., meta: { [SKIP_SERVER_ERROR_TOAST]: true } })
 */
export const SKIP_SERVER_ERROR_TOAST = 'skipServerErrorToast';

// i18n 초기화 전에는 t() 가 키를 그대로 돌려주므로 그때 쓸 폴백.
// 뮤테이션은 사용자 조작 이후에 실행되어 대개 초기화가 끝나 있지만 보장은 없다.
const FALLBACK_TITLE = 'A server error occurred.';
const FALLBACK_DESCRIPTION = 'Please try again in a moment.';

const translate = (key: string, fallback: string): string => {
  const translated = i18next.t(key, { ns: 'error' });

  return !translated || translated === key ? fallback : translated;
};

/**
 * 폼 제출(뮤테이션)이 서버 문제로 실패했을 때 토스트로 알린다.
 *
 * 뮤테이션은 쿼리와 달리 에러 바운더리로 넘기지 않는다: 전체 화면이 에러 페이지로
 * 교체되면 고객이 작성 중이던 가계약·결제 폼 입력값이 통째로 사라지기 때문이다.
 * 대신 화면과 입력값은 그대로 두고 토스트만 띄워 그 자리에서 다시 시도하게 한다.
 *
 * 안내를 아예 끄면 안 되는 이유: 예를 들어 usePostOrder 의 onError 는 ApiError 일 때만
 * 호출부로 전달하므로, 서버에 닿지 못해 생긴 실패는 아무 데서도 처리되지 않고 조용히
 * 사라진다. 고객은 제출 버튼을 눌렀는데 아무 일도 안 일어난 것처럼 보게 된다.
 *
 * 뮤테이션은 기본 재시도가 0회라 일시적인 통신 오류에도 바로 뜬다. 토스트는 화면을
 * 바꾸지 않으니 오탐이어도 손해가 없고, 제출이 안 됐다는 사실 자체는 알려야 한다.
 *
 * i18n 은 react 훅 밖이라 i18next 싱글톤을 직접 쓴다(src/i18n.js 가 초기화한 그 인스턴스).
 * error 네임스페이스는 부팅 시 미리 로딩된다(i18n.js 의 ns).
 */
export const notifyServerError = (error: unknown, meta?: Record<string, unknown>): void => {
  if (meta?.[SKIP_SERVER_ERROR_TOAST]) return;
  if (!isServerUnavailable(error)) return;

  toast.error(translate('serverError.title', FALLBACK_TITLE), {
    description: translate('serverError.description', FALLBACK_DESCRIPTION),
    position: 'top-center',
  });
};
